/**
 * api.js
 * 
 * Servicio para comunicarse con la API de Cloudflare Workers
 * 
 * Qué hace:
 * - Genera links únicos para repartidores
 * - Guarda la posición del repartidor
 * - Obtiene la posición del repartidor
 * - Verifica el estado del repartidor
 * 
 * Endpoints:
 * - POST /api/repartidor/generar-link - Generar nuevo link
 * - POST /api/repartidor/posicion - Guardar posición
 * - GET /api/repartidor/posicion/:linkId - Obtener posición
 * - GET /api/repartidor/estado/:linkId - Verificar estado
 */

// URL base de la API (desde variable de entorno o localhost para desarrollo)
// Vite solo expone variables que empiezan con VITE_
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787'

// Log detallado para desarrollo (solo en desarrollo)
if (import.meta.env.DEV) {
  console.log('🔧 Configuración de API:')
  console.log('   - VITE_API_URL desde env:', import.meta.env.VITE_API_URL || '(no definida)')
  console.log('   - API_URL final:', API_URL)
  console.log('   - Modo:', import.meta.env.MODE)
  console.log('   - Dev:', import.meta.env.DEV)
  console.log('   - Todas las vars VITE_:', Object.keys(import.meta.env).filter(k => k.startsWith('VITE_')))
  
  if (!import.meta.env.VITE_API_URL) {
    console.warn('⚠️ VITE_API_URL no está configurada en las variables de entorno.')
    console.warn('   SOLUCIÓN:')
    console.warn('   1. Crea un archivo .env.local en la raíz del proyecto (mismo nivel que package.json)')
    console.warn('   2. Agrega esta línea: VITE_API_URL=http://localhost:8787')
    console.warn('   3. REINICIA el servidor de desarrollo (Ctrl+C y luego npm run dev)')
    console.warn('   4. Verifica que el archivo .env.local esté en la raíz, no en src/')
  } else {
    console.log('✅ VITE_API_URL configurada correctamente:', import.meta.env.VITE_API_URL)
  }
  
  // Verificar si el Worker está corriendo
  console.log('🔍 Verificando conexión con Worker...')
  fetch(`${API_URL}/api/repartidor/generar-link`, { 
    method: 'POST',
    headers: { 'Content-Type': 'application/json' }
  })
    .then(() => console.log('✅ Worker está respondiendo'))
    .catch(() => {
      console.error('❌ Worker NO está respondiendo en', API_URL)
      console.error('   SOLUCIÓN: Abre otra terminal y ejecuta: npm run wrangler:dev')
    })
}

/**
 * Genera un nuevo link único para un repartidor
 * @returns {Promise<{id: string, linkId: string, url: string}>}
 */
export async function generarLinkRepartidor() {
  try {
    // Verificar que la URL de la API esté configurada
    if (!API_URL || API_URL === 'http://localhost:8787') {
      console.warn('⚠️ VITE_API_URL no está configurada. Usando localhost por defecto.')
      console.warn('   Configura VITE_API_URL en Cloudflare Pages → Environment Variables')
    }

    // Construir la URL completa
    const url = `${API_URL}/api/repartidor/generar-link`
    
    // Log para debugging en desarrollo
    if (import.meta.env.DEV) {
      console.log('🔗 Haciendo petición a:', url)
      console.log('   Método: POST')
      console.log('   API_URL:', API_URL)
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      // No enviar body - este endpoint no lo requiere
    })

    // Verificar si la respuesta es OK
    if (!response.ok) {
      // Intentar obtener el error como JSON
      let errorMessage = `Error ${response.status}: ${response.statusText}`
      try {
        const errorData = await response.json()
        errorMessage = errorData.error || errorMessage
      } catch (e) {
        // Si no es JSON, leer como texto
        try {
          const text = await response.text()
          if (text) {
            errorMessage = text
          }
        } catch (e2) {
          // Si todo falla, usar el mensaje por defecto
        }
      }
      
      // Mensajes más descriptivos según el código de estado
      if (response.status === 405) {
        errorMessage = 'Método no permitido. Verifica que el Worker esté desplegado correctamente.'
      } else if (response.status === 404) {
        errorMessage = 'Endpoint no encontrado. Verifica la URL del Worker.'
      } else if (response.status === 500) {
        errorMessage = 'Error del servidor. Verifica que la base de datos D1 esté configurada.'
      }
      
      throw new Error(errorMessage)
    }

    // Intentar parsear la respuesta como JSON
    let data
    try {
      const text = await response.text()
      if (!text) {
        throw new Error('Respuesta vacía del servidor')
      }
      data = JSON.parse(text)
    } catch (parseError) {
      console.error('Error parseando respuesta:', parseError)
      throw new Error('Respuesta inválida del servidor. Verifica que el Worker esté funcionando correctamente.')
    }

    return data
  } catch (error) {
    console.error('Error generando link:', error)
    
    // Mensajes más amigables para el usuario
    if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
      throw new Error('No se pudo conectar con el servidor. Verifica que el Worker esté desplegado y que VITE_API_URL esté configurada correctamente.')
    }
    
    throw error
  }
}

/**
 * Guarda la posición actual del repartidor
 * @param {string} linkId - ID del link del repartidor
 * @param {number} lat - Latitud
 * @param {number} lng - Longitud
 * @returns {Promise<{success: boolean, timestamp: number}>}
 */
export async function guardarPosicionRepartidor(linkId, lat, lng) {
  try {
    const response = await fetch(`${API_URL}/api/repartidor/posicion`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        linkId,
        lat,
        lng,
      }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Error al guardar posición')
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error guardando posición:', error)
    throw error
  }
}

/**
 * Obtiene la posición actual del repartidor
 * @param {string} linkId - ID del link del repartidor
 * @returns {Promise<{lat: number, lng: number, ultima_actualizacion: number, activo: boolean}>}
 */
export async function obtenerPosicionRepartidor(linkId) {
  try {
    const response = await fetch(`${API_URL}/api/repartidor/posicion/${linkId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Error al obtener posición')
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error obteniendo posición:', error)
    throw error
  }
}

/**
 * Verifica el estado del repartidor (activo/inactivo)
 * @param {string} linkId - ID del link del repartidor
 * @returns {Promise<{activo: boolean, ultima_actualizacion: number, tiempoInactivo: number}>}
 */
export async function obtenerEstadoRepartidor(linkId) {
  try {
    const response = await fetch(`${API_URL}/api/repartidor/estado/${linkId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Error al obtener estado')
    }

    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error obteniendo estado:', error)
    throw error
  }
}

